import { Col, Container, Row } from "@amzn/stencil-react-components/layout";
import { Link } from "@amzn/stencil-react-components/link";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { TabBar, TabPanel, TabSwitcher, useTabs } from "@amzn/stencil-react-components/tabs";
import { Text } from "@amzn/stencil-react-components/text";
import { withTooltip } from "@amzn/stencil-react-components/tooltip";
import { IconQuestionCircle } from "@amzn/stencil-react-icons";
import ListModalButton from "common/components/ListModal/ListModalButton";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { ListType } from "common/context/lists";
import { useLists } from "common/context/lists/ListsContext";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { ApplicableList, ListModel, Visibility } from "lib/models/list-models";
import { AmazonianInformation } from "lib/models/oui-models";
import { UserPermissions } from "lib/services";
import { UserInformation } from "lib/services/user-information-service";
import React, { FC, useEffect } from "react";
import { useHistory } from "react-router-dom";

import jobFamiliesMapping from "../../../configuration/AIForMe/jobFamiliesMapping.json";
import { List } from "./List";

const FAQ_SECTION = "lists";
const IconWithTooltip = withTooltip()(IconQuestionCircle);

const ListsPage: FC = () => {
  const history = useHistory();
  const {
    yourLists,
    sharedLists,
    sharedWithYouLists,
    loading,
    getYourLists,
    getSharedLists,
    getSharedWithYouLists,
    evaluateLists,
  } = useLists();
  const [recommendedLists, setRecommendedLists] = React.useState<ApplicableList[]>([]);
  const [loadingRecommended, setLoadingRecommended] = React.useState(false);
  const allowedDomain = window.location.href.includes("aiforme") ? "aiforme" : "software";
  const tabs = [
    getString("lists.tabs.yourLists"),
    getString("lists.tabs.sharedLists"),
    getString("lists.tabs.sharedWithYouLists"),
    ...(allowedDomain === "aiforme" ? [getString("lists.tabs.admin")] : []),
  ];

  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);
  const { tabBarProps, tabSwitcherProps } = useTabs({ tabs });

  useEffect(() => {
    if (toggleLists) {
      getYourLists({ includeItemDetails: true });
      getSharedLists({ includeItemDetails: true });
      getSharedWithYouLists({ includeItemDetails: true });
    }
  }, [toggleLists]);

  useEffect(() => {
    setRecommendedLists([]);
    const userInfo = UserInformation.current.getCachedAmazonianInformation() as AmazonianInformation;
    const jobFamily = (jobFamiliesMapping as { [key: string]: string })[userInfo?.jobInfo?.jobFamilyName];

    if (!jobFamily) return;

    setLoadingRecommended(true);
    evaluateLists({ jobFamilyName: [jobFamily] }, { includeItemDetails: true })
      .then((lists) => setRecommendedLists(lists))
      .finally(() => setLoadingRecommended(false));
  }, [allowedDomain]);

  if (!toggleLists) {
    history.push("/");
    return null;
  }

  const renderLists = (
    lists: ListModel[],
    listType: ListType,
    allowDeletion?: boolean,
    allowEdit?: boolean,
    allowItemDeletion?: boolean
  ) => {
    return (
      <Col gridGap="S300">
        {loading && <Spinner />}
        {!loading &&
          lists.map(
            (list) =>
              list.tags.includes("favorite") &&
              list.allowedDomains.includes(allowedDomain) && (
                <List key={`Favorite-List`} listType={listType} {...list} allowItemDeletion={true} />
              )
          )}
        {!loading &&
          lists.map(
            (list, index) =>
              list.allowedDomains.includes(allowedDomain) &&
              !list.tags.includes("favorite") && (
                <List
                  key={`List-${index}`}
                  listType={listType}
                  allowDeletion={allowDeletion}
                  allowEdit={allowEdit}
                  allowItemDeletion={allowItemDeletion}
                  {...list}
                />
              )
          )}
        {!loading && lists.length == 0 && <Text fontSize="T200">{getString("lists.noLists")}</Text>}
      </Col>
    );
  };

  const renderPublicLists = () => {
    return (
      <Col gridGap="S300">
        {loadingRecommended && <Spinner />}
        {!loadingRecommended &&
          recommendedLists.length > 0 &&
          recommendedLists.map((list, index) => (
            <List listType={ListType.PUBLIC_LISTS} key={`public-list-${index}`} {...list} />
          ))}
        {!loadingRecommended && recommendedLists.length === 0 && (
          <Text fontSize="T200">{getString("lists.noLists")}</Text>
        )}
      </Col>
    );
  };

  return (
    <Col>
      <PageMainContainer>
        <Container paddingTop={32} paddingBottom={32}>
          <Col gridGap={32}>
            <Row justifyContent="space-between">
              <Text fontSize="T400" fontWeight="bold">
                {getString("lists.title")}
              </Text>
              <ListModalButton />
            </Row>
            <Row alignItems="center" gridGap="S100">
              <Text fontSize="T200">{getString("lists.description")}</Text>
              <IconWithTooltip
                tooltipText={
                  <Text>
                    {getString("lists.faqLinkDescription")}{" "}
                    <Link href={`/faq?section=${FAQ_SECTION}`} target="_blank">
                      {getString("lists.faqLinkText")}
                    </Link>
                  </Text>
                }
                color="primary70"
              />
            </Row>

            <TabBar {...tabBarProps} />
            <TabSwitcher {...tabSwitcherProps}>
              <TabPanel value={getString("lists.tabs.yourLists")}>
                {renderLists(yourLists, ListType.YOUR_LISTS, true, true, true)}
              </TabPanel>
              <TabPanel value={getString("lists.tabs.sharedLists")}>
                {renderLists(sharedLists, ListType.SHARED_LISTS, true, true, true)}
              </TabPanel>
              <TabPanel value={getString("lists.tabs.sharedWithYouLists")}>
                {renderLists(sharedWithYouLists, ListType.SHARED_WITH_YOU_LISTS, false, false, false)}
              </TabPanel>
              {allowedDomain === "aiforme" && (
                <TabPanel value={getString("lists.tabs.admin")}>{renderPublicLists()}</TabPanel>
              )}
            </TabSwitcher>
          </Col>
        </Container>
      </PageMainContainer>
    </Col>
  );
};

export default ListsPage;
